import { useCallback, useMemo } from 'react';
import { ASC, DESC } from 'common/constants/analyst-dashboards';
import { useLocalStorageByUser } from 'common/hooks';
import { SortCriteria } from 'dashboard409a/common/constants/analyst-dashboard';

export interface UseMultiColumnSortProps {
  storageKey: string;
  defaultOrderBy: string;
  defaultOrder: 'asc' | 'desc';
}

export interface UseMultiColumnSortReturn {
  sortCriteria: SortCriteria[];
  orderBy: string;
  order: 'asc' | 'desc';
  isReady: boolean;
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string, addToSort?: boolean) => void;
}

interface StoredSortPreference {
  sortCriteria?: SortCriteria[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const normalizeSortCriteria = (
  storedPreference: StoredSortPreference,
  defaultOrderBy: string,
  defaultOrder: 'asc' | 'desc'
): SortCriteria[] => {
  if (storedPreference.sortCriteria?.length) {
    return storedPreference.sortCriteria;
  }
  if (storedPreference.sortBy) {
    return [{
      property: storedPreference.sortBy,
      direction: storedPreference.sortOrder ?? defaultOrder,
    }];
  }
  return [{ property: defaultOrderBy, direction: defaultOrder }];
};

export const useMultiColumnSort = ({
  storageKey,
  defaultOrderBy,
  defaultOrder,
}: UseMultiColumnSortProps): UseMultiColumnSortReturn => {
  const defaultPreference = useMemo<StoredSortPreference>(
    () => ({ sortCriteria: [{ property: defaultOrderBy, direction: defaultOrder }] }),
    [defaultOrder, defaultOrderBy]
  );
  const [storedPreference, setStoredPreference, isReady] = useLocalStorageByUser(
    storageKey,
    defaultPreference
  );
  const sortCriteria = useMemo(
    () => normalizeSortCriteria(storedPreference, defaultOrderBy, defaultOrder),
    [defaultOrder, defaultOrderBy, storedPreference]
  );

  // Derive orderBy and order from first sort criteria for backward compatibility
  const orderBy = sortCriteria.length > 0 ? sortCriteria[0].property : defaultOrderBy;
  const order = sortCriteria.length > 0 ? sortCriteria[0].direction : defaultOrder;

  const handleRequestSort = useCallback(
    (event: React.MouseEvent<unknown>, property: string, addToSort?: boolean) => {
      const MAX_SORT_COLUMNS = 2;

      if (addToSort) {
        // Add to existing sorts (Shift+Click)
        setStoredPreference(previousPreference => {
          const prev = normalizeSortCriteria(previousPreference, defaultOrderBy, defaultOrder);
          const existingIndex = prev.findIndex(criteria => criteria.property === property);

          if (existingIndex >= 0) {
            const newCriteria = [...prev];
            const existing = newCriteria[existingIndex];

            if (existing.direction === 'asc') {
              // Change to desc
              newCriteria[existingIndex] = { ...existing, direction: 'desc' };
              return { sortCriteria: newCriteria };
            }
            // Remove from sort (was desc, now remove)
            newCriteria.splice(existingIndex, 1);
            return {
              sortCriteria: newCriteria.length > 0
                ? newCriteria
                : [{ property, direction: 'asc' }],
            };
          }

          // Property doesn't exist - add it with asc direction
          if (prev.length >= MAX_SORT_COLUMNS) {
            // At max columns - remove the first column and add the new one at the end
            return { sortCriteria: [...prev.slice(1), { property, direction: 'asc' }] };
          }
          return { sortCriteria: [...prev, { property, direction: 'asc' }] };
        });
      } else {
        // Replace all sorts (regular click)
        const existingIndex = sortCriteria.findIndex(criteria => criteria.property === property);
        const isAsc = existingIndex >= 0 && sortCriteria[existingIndex].direction === 'asc';

        setStoredPreference({ sortCriteria: [{ property, direction: isAsc ? DESC : ASC }] });
      }
    },
    [defaultOrder, defaultOrderBy, setStoredPreference, sortCriteria]
  );

  return {
    sortCriteria,
    orderBy,
    order,
    isReady,
    handleRequestSort,
  };
};
